import useFormatter from "@/use/formatter";
import store from "@/store";

export default function useLoanConfigurationHelpers() {
  const { formatGraduationDateDisplay } = useFormatter();

  function clearLoanConfigurationCache() {
    const cacheKey = "ApplicantPortal";
    const referenceId = store?.state?.appData?.application?.referenceId ?? "";

    const keys = [
      "anticipatedGraduationDateUpdated",
      "enrollmentTermUpdated",
      "enrollmentStatusUpdated",
      "schoolUpdated",
      "borrowAmountUpdated",
      "loanConfigurationUpdated",
    ];

    keys.forEach((k: string) => {
      localStorage.removeItem(`${cacheKey}${referenceId}.${k}`);
    });
  }

  function setCacheItem(identifier: string, value: boolean) {
    const cacheKey = "ApplicantPortal";
    const referenceId = store?.state?.appData?.application?.referenceId ?? "";

    const legalPacketGenerated = store.getters["loanConfigurationModule/legalPacketGenerated"];

    // use utc timestamp
    const currentDateTime = new Date();

    if (legalPacketGenerated) {
      localStorage.setItem(
        `${cacheKey}${referenceId}.${identifier}`,
        JSON.stringify({
          timestamp: currentDateTime.toUTCString(),
          value,
        }),
      );
    }
  }

  function compareDates(date1: Date, date2: Date) {
    if (date1.getTime() < date2.getTime()) {
      console.log("date1 is earlier than date2");
      return true;
    } else if (date1.getTime() > date2.getTime()) {
      console.log("date1 is later than date2");
      return false;
    } else {
      console.log("date1 and date2 are the same");
      return false;
    }
  }

  function getCacheItem(identifier: string) {
    const cacheKey = "ApplicantPortal";
    const referenceId = store?.state?.appData?.application?.referenceId ?? "";
    const item = localStorage.getItem(`${cacheKey}${referenceId}.${identifier}`);
    const legalPacketCreatedAtUtc =
      store.getters["loanConfigurationModule/legalPacketCreatedAtUtc"];

    if (item) {
      const jsonItem = JSON.parse(item);
      const compare = compareDates(new Date(legalPacketCreatedAtUtc), new Date(jsonItem.timestamp));

      return compare;
    }

    return false;
  }

  function hasGraduationDateChanges(
    originalGraduationDate: null | string | Date,
    newGraduationDate: string | Date,
  ): boolean {
    const hasChanges =
      (originalGraduationDate !== null && originalGraduationDate !== newGraduationDate) ||
      originalGraduationDate === null;

    if (hasChanges) {
      setCacheItem("anticipatedGraduationDateUpdated", hasChanges);
    }

    return hasChanges;
  }

  function hasEnrollmentStatusChanges(
    originalEnrollmentStatusId: number,
    newEnrollmentStatusId: number,
  ) {
    const hasChanges =
      (originalEnrollmentStatusId !== null &&
        originalEnrollmentStatusId !== newEnrollmentStatusId) ||
      originalEnrollmentStatusId === null;

    if (hasChanges) {
      setCacheItem("enrollmentStatusUpdated", hasChanges);
    }

    return hasChanges;
  }

  function hasEnrollmentTermChanges(originalEnrollmentTerm: any, newEnrollmentTerm: any): boolean {
    const hasChanges =
      (originalEnrollmentTerm !== null && originalEnrollmentTerm !== newEnrollmentTerm) ||
      originalEnrollmentTerm === null;

    if (hasChanges) {
      setCacheItem("enrollmentTermUpdated", hasChanges);
    }

    return hasChanges;
  }

  function hasSchoolChanges(originalSchool: any, newSchool: any): boolean {
    const hasChanges =
      (originalSchool !== null && originalSchool !== newSchool) || originalSchool === null;

    if (hasChanges) {
      setCacheItem("schoolUpdated", hasChanges);
    }

    return hasChanges;
  }

  function hasBorrowAmountChanges(
    originalBorrowAmount: number | null,
    newBorrowAmount: number | string | null,
  ): boolean {
    const hasChanges =
      (originalBorrowAmount !== null && originalBorrowAmount !== newBorrowAmount) ||
      originalBorrowAmount === null;

    if (hasChanges) {
      setCacheItem("borrowAmountUpdated", hasChanges);
    }
    return hasChanges;
  }

  function hasLoanConfigurationChanges(
    originalLoanConfiguration: any,
    newLoanConfigurationRequest: any,
  ): boolean {
    const hasChanges =
      (originalLoanConfiguration !== null &&
        (originalLoanConfiguration.ratePlanId !== newLoanConfigurationRequest.selectedRatePlanId ||
          originalLoanConfiguration.repaymentTypeId !==
            newLoanConfigurationRequest.repaymentTypeId)) ||
      originalLoanConfiguration === null;

    if (hasChanges) {
      setCacheItem("loanConfigurationUpdated", hasChanges);
    }

    return hasChanges;
  }

  function checkSchoolConfirmationModalChanges(
    originalSchoolConfig: any,
    newSchoolConfig: any,
  ): boolean {
    const gradDateChanged = hasGraduationDateChanges(
      formatGraduationDateDisplay(originalSchoolConfig.graduationDate),
      formatGraduationDateDisplay(newSchoolConfig.graduationDate),
    );

    const enrollmentTermChanged = hasEnrollmentTermChanges(
      originalSchoolConfig.academicTerm,
      newSchoolConfig.academicTerm,
    );

    const schoolChanged = hasSchoolChanges(originalSchoolConfig.schoolId, newSchoolConfig.schoolId);

    const enrollmentStatusChanged = hasEnrollmentStatusChanges(
      originalSchoolConfig.enrollmentStatus,
      newSchoolConfig.enrollmentStatusId,
    );

    const hasChanges =
      gradDateChanged || enrollmentTermChanged || schoolChanged || enrollmentStatusChanged;

    return hasChanges;
  }

  function regenerateLegalPacketCheck(): boolean {
    const schoolConfigChanged = getCacheItem("schoolConfigurationUpdated");
    const borrowAmountChanged = getCacheItem("borrowAmountUpdated");
    const loanConfigurationUpdated = getCacheItem("loanConfigurationUpdated");

    return schoolConfigChanged || borrowAmountChanged || loanConfigurationUpdated;
  }

  return {
    hasBorrowAmountChanges,
    hasLoanConfigurationChanges,
    hasGraduationDateChanges,
    hasEnrollmentTermChanges,
    hasEnrollmentStatusChanges,
    hasSchoolChanges,
    checkSchoolConfirmationModalChanges,
    regenerateLegalPacketCheck,
    setCacheItem,
    clearLoanConfigurationCache,
  };
}
